/**
 * Story-director — sibling of the chat-director. Grows narrative from world
 * ground-truth via the same batched/budgeted-proxy pattern, behind an injected
 * Voice, and degrades to an authored floor when the LLM is off / offline / over
 * budget. It PHRASES canon; it never invents hard mechanics, never makes anything
 * missable, and never pressures completion (D7 + the triple contract).
 *
 * The renderer/society own truth; this layer only turns LoreBeats into ambient,
 * optional narration. All randomness flows through the injected core/rng Rng.
 */
import type { Rng } from '@/core/rng.js';
import type {
  StoryRequest,
  StoryResponse,
  LoreBeat,
  CodexEntry,
  PlayerContext,
  SpeakerContext,
  VoiceLine,
} from '@/shared';

/**
 * A lore-flavored Voice: same shape as the social Voice but over StoryRequest/
 * StoryResponse (which carry codex-growth prose). Both an LLM backend and the
 * authored TemplateStoryVoice satisfy it. Implementations must never throw on
 * degradation — a quiet day is a valid, lovely day.
 */
export interface StoryVoice {
  readonly kind: 'llm' | 'template';
  generate(request: StoryRequest): Promise<StoryResponse>;
}

/**
 * Pick the lore beats worth narrating now, newest-first, capped at maxBeats.
 * Pure + deterministic: ties broken by id so the same pool always yields the
 * same selection. Nothing here is urgent — unpicked beats are simply not
 * mentioned this time, never lost.
 */
export function selectLoreBeats(
  available: readonly LoreBeat[],
  maxBeats: number,
): readonly LoreBeat[] {
  if (maxBeats <= 0 || available.length === 0) return [];
  const sorted = available.slice().sort((a, b) => {
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt; // fresher first
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  return sorted.slice(0, maxBeats);
}

/** Assemble a single batched story-director request from selected beats. */
export function buildStoryRequest(
  beats: readonly LoreBeat[],
  speakers: readonly SpeakerContext[],
  player: PlayerContext,
  desiredLineCount: number,
  tokenBudget: number,
): StoryRequest {
  return { beats, speakers, player, tokenBudget, desiredLineCount };
}

/**
 * Orchestrate one batch of narration. Uses the injected Voice when budget allows,
 * else (or on any failure) renders the authored floor. Never throws.
 */
export async function runStoryDirector(
  request: StoryRequest,
  voice: StoryVoice,
  rng: Rng,
): Promise<StoryResponse> {
  const floor = new TemplateStoryVoice(rng.fork('story-floor'));

  // Over budget, or an LLM Voice with no budget headroom ⇒ stay on the floor and
  // never spend a call. The world stays warm and chatty regardless.
  if (request.tokenBudget <= 0 && voice.kind === 'llm') {
    return floor.generate(request);
  }

  try {
    return await voice.generate(request);
  } catch {
    return floor.generate(request);
  }
}

/**
 * Fold grown prose back onto the codex it belongs to. Appends to the existing
 * authored body (canon is preserved, never overwritten), ignores prose for
 * unknown thread ids (never fabricates a new entry), and returns a fresh array
 * without mutating the input.
 */
export function applyCodexGrowth(
  entries: readonly CodexEntry[],
  response: StoryResponse,
): readonly CodexEntry[] {
  const prose = response.codexProse;
  if (!prose || prose.length === 0) return entries;

  const additions = new Map<string, string[]>();
  for (const p of prose) {
    if (!entries.some((e) => e.id === p.threadId)) continue; // ignore orphans
    const list = additions.get(p.threadId) ?? [];
    list.push(p.body);
    additions.set(p.threadId, list);
  }
  if (additions.size === 0) return entries;

  return entries.map((e) => {
    const extra = additions.get(e.id);
    if (!extra || extra.length === 0) return e;
    return { ...e, body: `${e.body}\n\n${extra.join('\n\n')}` };
  });
}

// --- The authored floor -----------------------------------------------------

/** Gentle openers/closers per lore-beat kind. Warm, never urgent. */
const FLOOR_GRAMMAR: Readonly<Record<string, readonly string[]>> = {
  'roaming-rumor': [
    'Someone swears they felt {subject} pass over the far ridge today. It keeps its own seasons, in its own time.',
    'There is talk of {subject} drifting through the region again. Lovely thought, isn’t it?',
    'A traveler mentioned {subject} on the wind. Maybe true, maybe a tall tale. Either way, the sky is kind today.',
  ],
  'town-legend': [
    'The town has been retelling the story of {subject}, the way it always does on a slow afternoon.',
    'Folks gathered by the well to murmur again about {subject}. An old comfort, that story.',
    'They keep a soft place in their tales for {subject}. It never gets old around here.',
  ],
  chronicle: [
    'Your own chronicle gained a quiet line today: {subject}.',
    'The keeper noted {subject} in your page — no scores, just the memory of it.',
    'A small, good thing was written down for you: {subject}.',
  ],
  'codex-unlock': [
    'A new page opened in the codex, all on its own: {subject}. Read it whenever you like.',
    'The region offered up another of its old stories — {subject} — for some evening you feel like wandering into it.',
  ],
};

const FLOOR_DEFAULT = [
  'The region settled into a gentle quiet, and that was a fine thing too: {subject}.',
];

function fillTemplate(tpl: string, beat: LoreBeat): string {
  const subject = String(beat.data.subject ?? beat.threadId ?? beat.id ?? 'the day');
  return tpl.replaceAll('{subject}', subject);
}

/** Pick a gentle floor template for a beat's kind (deterministic given the rng). */
function pickTpl(beat: LoreBeat, rng: Rng): string {
  const bank = FLOOR_GRAMMAR[beat.kind] ?? FLOOR_DEFAULT;
  return bank[rng.int(bank.length)]!;
}

/**
 * The authored fallback floor: turns LoreBeats into warm, grounded narration with
 * zero tokens and zero network. Deterministic given its seed. Guaranteed to read
 * as calm and never to pressure the player. It IS the floor — it never throws.
 */
export class TemplateStoryVoice implements StoryVoice {
  readonly kind = 'template' as const;
  private readonly rng: Rng;

  constructor(rng: Rng) {
    this.rng = rng;
  }

  generate(request: StoryRequest): Promise<StoryResponse> {
    const narratorId = request.speakers[0]?.trainerId ?? 'narrator';
    const want = Math.max(0, request.desiredLineCount);
    const lines: VoiceLine[] = [];

    for (const beat of request.beats) {
      if (lines.length >= want) break;
      // A beat may carry an AUTHORED line verbatim (e.g. the Mewtwo thread's seed
      // lines) — phrase it exactly, so the floor stays true to the authored canon.
      // Otherwise fall back to the gentle per-kind grammar. Never urgent, never a nag.
      const authored = typeof beat.data.line === 'string' ? beat.data.line.trim() : '';
      const text = authored.length > 0 ? authored : fillTemplate(pickTpl(beat, this.rng), beat);
      lines.push({
        trainerId: narratorId,
        emotion: 'normal',
        text,
      });
    }

    return Promise.resolve({
      lines,
      source: 'template',
      tokensUsed: 0,
    });
  }
}
